import * as LabelPrimitive from '@radix-ui/react-label';
import type { ReactNode } from 'react';
import { cn } from '../lib/utils.js';
import { Input, type InputProps } from './Input.js';

/** Label built on Radix — wires the label to the control via `htmlFor`. */
export function Label({ className, ...rest }: LabelPrimitive.LabelProps) {
  return <LabelPrimitive.Label className={cn('text-sm font-bold text-fg', className)} {...rest} />;
}

export interface FieldProps {
  /** Rendered inside the label element (text, or text + badge). */
  readonly label: ReactNode;
  /** Input id — also links label and control for screen readers. */
  readonly id: string;
  /** Single-line helper text shown under the input. */
  readonly hint?: ReactNode;
  /** Error message; sets aria-invalid + aria-describedby on the input. */
  readonly error?: string;
  /** Additional classes forwarded to the input element. */
  readonly inputClassName?: string;
  readonly inputProps?: Omit<InputProps, 'id' | 'className'> & {
    /** data-* attributes (testids, appdom ids) pass straight through. */
    readonly [dataAttr: `data-${string}`]: unknown;
  };
}

/**
 * Label + input + hint/error stack — the one way forms are built in this
 * codebase (audit F2: forms were hand-styled per screen).
 */
export function Field({ label, id, hint, error, inputClassName, inputProps }: FieldProps) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={inputClassName}
        {...inputProps}
      />
      {error ? (
        <p id={`${id}-error`} className="text-sm font-semibold text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-sm text-fg-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
